import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDown, ArrowLeft, ArrowUp, BadgeCheck, ExternalLink, FilePlus2, Globe, Palette, Pencil, Play, RefreshCw, Trash2,
} from "lucide-react";
import { api, type Demo, type DemoEstimate, type DemoItem, type MakeCustomerResult, type Tenant } from "../api";
import { ConfirmModal, Modal, PromptModal, type PromptField } from "../components/Modal";
import { TenantThemeModal } from "../components/TenantThemeModal";
import { CopyLink, demoState, endOfDay } from "./Demos";
import { Alert, PageHeader } from "../components/Page";

function pad(n: number): string { return String(n).padStart(2, "0"); }

function toDateInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fileSize(n: number): string {
  return n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

function rangeText(r: string): string { return r.replace("-", "–"); }

/** The first block of the item id — the page's #open-<anchor> deep link. */
function anchorOf(item: DemoItem): string { return item.id.split("-")[0]; }

function ItemStatus({ item }: { item: DemoItem }) {
  const s = item.conversion_status;
  const parts: JSX.Element[] = [];
  if (!s) parts.push(<span key="n" className="pill-discovered">Not converted</span>);
  else if (s === "queued") parts.push(<span key="q" className="pill-queued">Queued</span>);
  else if (s === "running") parts.push(<span key="r" className="pill-running">Converting</span>);
  else if (s === "failed") parts.push(<span key="f" className="pill-failed">Failed</span>);
  if (item.published) parts.push(<span key="p" className="pill-succeeded">On the page</span>);
  if (item.review_required) {
    parts.push(
      <span key="h" className="pill-review">
        {item.published ? "New version held for review" : "Held for review"}
        {item.open_blocking ? ` · ${item.open_blocking} to check` : ""}
      </span>,
    );
  } else if (!item.published && (s === "succeeded" || s === "degraded")) {
    parts.push(<span key="x" className="pill-degraded">Not shown</span>);
  }
  return <span className="inline-flex flex-wrap gap-1.5">{parts}</span>;
}

// -------- converting --------------------------------------------------

type Mode = "first" | "range" | "all";

function ConvertModal({ demo, item, onClose, onDone }: {
  demo: Demo; item: DemoItem; onClose: () => void; onDone: (it: DemoItem) => void;
}) {
  const big = item.page_count > 50;
  const [mode, setMode] = useState<Mode>(big ? "first" : "all");
  const [firstN, setFirstN] = useState(String(Math.min(50, item.page_count)));
  const [from, setFrom] = useState("1");
  const [to, setTo] = useState(String(Math.min(50, item.page_count)));
  const [est, setEst] = useState<DemoEstimate | null>(null);
  const [estErr, setEstErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const seq = useRef(0);

  const pages = mode === "all" ? null : mode === "first" ? `1-${firstN}` : `${from}-${to}`;

  useEffect(() => {
    const mine = ++seq.current;
    setEst(null); setEstErr(null);
    const t = window.setTimeout(async () => {
      try {
        const e = await api.estimateDemoItem(demo.id, item.id, pages);
        if (mine === seq.current) setEst(e);
      } catch (x) {
        if (mine === seq.current) setEstErr(x instanceof Error ? x.message : String(x));
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [demo.id, item.id, pages]);

  const overLimit = est !== null && est.pages_left !== null && est.pages > est.pages_left;

  async function go() {
    if (!est) return;
    setBusy(true); setErr(null);
    try { onDone(await api.convertDemoItem(demo.id, item.id, pages, est.estimate_usd)); }
    catch (x) { setErr(x instanceof Error ? x.message : String(x)); }
    finally { setBusy(false); }
  }

  const radio = (value: Mode, label: string) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="radio" name="cv-mode" value={value} checked={mode === value} onChange={() => setMode(value)} />
      {label}
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`Convert: ${item.label}`}
      size="lg"
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={go} disabled={!est || busy || overLimit}>
          {busy ? <><span className="spinner" aria-hidden="true" /> Starting…</>
            : est ? `Convert ${est.pages} page${est.pages === 1 ? "" : "s"} for about $${est.estimate_usd.toFixed(2)}`
            : "Convert"}
        </button>
      </>}
    >
      <p className="text-sm text-slate-600">
        <strong>{item.filename}</strong> has {item.page_count} page{item.page_count === 1 ? "" : "s"}.
        {big && " For a long document, converting the first part is usually enough for a demo."}
      </p>
      <fieldset className="mt-4 space-y-3">
        <legend className="text-sm font-medium text-slate-700 mb-1">Pages to convert</legend>
        {big && (
          <div className="flex flex-wrap items-center gap-2">
            {radio("first", "The first")}
            <label htmlFor="cv-first" className="sr-only">Number of pages from the start</label>
            <input id="cv-first" className="input !w-24" type="number" min={1} max={item.page_count}
              value={firstN} onChange={(e) => { setFirstN(e.target.value); setMode("first"); }} />
            <span className="text-sm">pages</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {radio("range", "Pages")}
          <label htmlFor="cv-from" className="sr-only">First page</label>
          <input id="cv-from" className="input !w-24" type="number" min={1} max={item.page_count}
            value={from} onChange={(e) => { setFrom(e.target.value); setMode("range"); }} />
          <span className="text-sm">to</span>
          <label htmlFor="cv-to" className="sr-only">Last page</label>
          <input id="cv-to" className="input !w-24" type="number" min={1} max={item.page_count}
            value={to} onChange={(e) => { setTo(e.target.value); setMode("range"); }} />
        </div>
        {radio("all", `All ${item.page_count} pages`)}
      </fieldset>
      {mode === "range" && Number(from) > 1 && (
        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          The accessible version numbers its pages from 1, so page {from} of the original shows as page 1.
        </p>
      )}

      <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm" aria-live="polite">
        {estErr ? <p className="text-rose-700">{estErr}</p>
          : !est ? <p className="text-slate-500 flex items-center gap-2"><span className="spinner" aria-hidden="true" /> Working out the cost…</p>
          : <>
            <p>
              <strong>{est.pages} page{est.pages === 1 ? "" : "s"}</strong>
              {est.page_range ? ` (pages ${rangeText(est.page_range)} of ${est.page_count})` : ""} · about{" "}
              <strong>${est.estimate_usd.toFixed(2)}</strong>, up to ${est.max_usd.toFixed(2)} for scanned or dense pages.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {est.rate_based_on_pages > 0 && est.rate_based_on_pages >= 100
                ? `$${est.usd_per_page.toFixed(4)} per page, measured over ${est.rate_based_on_pages.toLocaleString()} converted pages.`
                : `$${est.usd_per_page.toFixed(4)} per page (default until enough pages have been converted to measure it).`}
              {est.pages_left !== null && ` ${est.pages_left.toLocaleString()} pages left in this demo's limit.`}
            </p>
            {overLimit && (
              <p className="text-rose-700 mt-2">
                That's more than the {est.pages_left} pages left in this demo's limit. Pick fewer pages or raise the limit.
              </p>
            )}
          </>}
      </div>
      {err && <p role="alert" className="mt-3 text-sm text-rose-700">{err}</p>}
    </Modal>
  );
}

// -------- the client's website and becoming a customer ---------------

function ClientSiteCard({ demo, onCrawl }: { demo: Demo; onCrawl: () => void }) {
  const site = demo.client_site;
  const crawling = site?.status === "crawling";
  return (
    <section className="card mb-6" aria-labelledby="site-title">
      <div className="card-header">
        <h2 id="site-title" className="text-base font-semibold text-ink flex items-center gap-2">
          <Globe className="h-4 w-4 text-brand-500" aria-hidden="true" /> Client&apos;s website
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Crawling finds every PDF on their site for the Conversion plan (scanned, untagged, forms, priority and cost).
          It converts nothing, runs once when you ask, and is never repeated on a schedule.
        </p>
      </div>
      <div className="card-body flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="min-w-0">
          {demo.client_website
            ? <a href={demo.client_website} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline break-all">{demo.client_website}</a>
            : <span className="text-slate-500">No website set. Add it in Page settings below.</span>}
          {site && (
            <div className="text-xs text-slate-500 mt-1">
              <span className={`pill-${site.status}`}>{site.status}</span>{" "}
              {site.last_scanned_at
                ? <>{demo.discovered_count.toLocaleString()} PDFs found · last crawled {new Date(site.last_scanned_at).toLocaleString()} · open Conversion plan and pick {site.domain}</>
                : crawling ? "Crawling now. This can take a while on a big site." : "Not crawled yet."}
            </div>
          )}
        </div>
        <button type="button" className="btn-secondary" onClick={onCrawl} disabled={!demo.client_website || crawling || !demo.enabled}>
          {crawling
            ? <><span className="spinner" aria-hidden="true" /> Crawling…</>
            : <><RefreshCw className="h-4 w-4" aria-hidden="true" /> {site?.last_scanned_at ? "Crawl again" : "Crawl website"}</>}
        </button>
      </div>
    </section>
  );
}

function MakeCustomerModal({ demo, onClose, onDone }: {
  demo: Demo; onClose: () => void; onDone: (r: MakeCustomerResult) => void;
}) {
  const [name, setName] = useState(demo.client_name);
  const [website, setWebsite] = useState(demo.client_website ?? "");
  const [quota, setQuota] = useState(String(Math.max(demo.tenant.page_quota ?? 0, 1000)));
  const [keepPage, setKeepPage] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const partial = demo.items.filter((i) => i.page_range).length;

  async function go() {
    const q = quota.trim() === "" ? null : Number(quota);
    if (q !== null && (!Number.isInteger(q) || q < 1)) { setErr("The page limit must be a whole number, or blank for no limit."); return; }
    setBusy(true); setErr(null);
    try { onDone(await api.makeCustomer(demo.id, { name: name.trim(), website: website.trim(), page_quota: q, keep_page: keepPage })); }
    catch (x) { setErr(x instanceof Error ? x.message : String(x)); }
    finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={`Make ${demo.client_name} a customer`} size="lg"
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={go} disabled={busy || name.trim().length < 2 || !website.trim()}>
          {busy ? <><span className="spinner" aria-hidden="true" /> Working…</> : "Make customer"}
        </button>
      </>}
    >
      <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
        <li>Their demo workspace becomes their real one. Every conversion is kept and nothing is converted again.</li>
        <li>Whole-PDF conversions move to their website, so helper.js serves them there once it is installed.</li>
        {partial > 0 && (
          <li>
            {partial} part-PDF conversion{partial === 1 ? "" : "s"} stay demo-only: on their live site they would pass a few pages
            off as the whole document. Convert the full PDFs from the Conversion plan.
          </li>
        )}
        <li>It stops being a demo workspace: their people can add, convert and crawl within the page limit, and the demo page can no longer suspend it.</li>
      </ul>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div>
          <label htmlFor="mc-name" className="block text-sm font-medium text-slate-700 mb-1">Workspace name</label>
          <input id="mc-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="mc-site" className="block text-sm font-medium text-slate-700 mb-1">Their website</label>
          <input id="mc-site" className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://wheatonparkdistrict.com" />
        </div>
        <div>
          <label htmlFor="mc-quota" className="block text-sm font-medium text-slate-700 mb-1">Page limit</label>
          <input id="mc-quota" className="input" type="number" min={1} value={quota} onChange={(e) => setQuota(e.target.value)}
            aria-describedby="mc-quota-help" />
          <p id="mc-quota-help" className="mt-1 text-xs text-slate-500">Their contracted pages. Blank = no limit.</p>
        </div>
        <div className="flex items-center md:pt-6">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={keepPage} onChange={(e) => setKeepPage(e.target.checked)} /> Keep the demo page online
          </label>
        </div>
      </div>
      {err && <p role="alert" className="mt-3 text-sm text-rose-700">{err}</p>}
    </Modal>
  );
}

// -------- the screen --------------------------------------------------

export function DemoDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [demo, setDemo] = useState<Demo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Settings form
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [client, setClient] = useState("");
  const [website, setWebsite] = useState("");
  const [expires, setExpires] = useState("");
  const [quota, setQuota] = useState("");
  const [qa, setQa] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add-document form
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  const [convertFor, setConvertFor] = useState<DemoItem | null>(null);
  const [editFor, setEditFor] = useState<DemoItem | null>(null);
  const [removeFor, setRemoveFor] = useState<DemoItem | null>(null);
  const [themeFor, setThemeFor] = useState<Tenant | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [confirmCrawl, setConfirmCrawl] = useState(false);
  const [makeCustomerOpen, setMakeCustomerOpen] = useState(false);
  const [customerResult, setCustomerResult] = useState<MakeCustomerResult | null>(null);

  // Memoised: PromptModal resets its inputs whenever `fields` changes.
  const editFields = useMemo<PromptField[]>(() => (editFor ? [
    { name: "label", label: "Label", initialValue: editFor.label, required: true },
    { name: "title", label: "Title on the page (blank = the document's own title)", initialValue: editFor.title ?? "" },
    { name: "note", label: "Note for the client", initialValue: editFor.note ?? "" },
    { name: "source_url", label: "Original PDF link", type: "url", initialValue: editFor.source_url ?? "" },
  ] : []), [editFor]);

  const fillForm = useCallback((d: Demo) => {
    setTitle(d.title); setIntro(d.intro ?? ""); setClient(d.client_name);
    setWebsite(d.client_website ?? ""); setExpires(toDateInput(d.expires_at));
    setQuota(d.tenant.page_quota != null ? String(d.tenant.page_quota) : ""); setQa(d.tenant.qa_enabled);
  }, []);

  const load = useCallback(async (fill = false) => {
    try {
      const d = await api.getDemo(id);
      setDemo(d); setError(null);
      if (fill) fillForm(d);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [id, fillForm]);
  useEffect(() => { load(true); }, [load]);

  // Follow conversions while any is queued or running.
  const busy = (demo?.items.some((i) => i.conversion_status === "queued" || i.conversion_status === "running") ?? false)
    || demo?.client_site?.status === "crawling";
  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(() => { load(false); }, 10000);
    return () => window.clearInterval(t);
  }, [busy, load]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  if (error && !demo) {
    return <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded">{error}</div>;
  }
  if (!demo) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><span className="spinner" aria-hidden="true" /> Loading…</div>;
  }
  const d = demo;
  const st = demoState(d);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const q = Number(quota);
    if (!Number.isInteger(q) || q < 1) { setError("The page limit must be a whole number of 1 or more."); return; }
    setSaving(true); setError(null);
    try {
      const updated = await api.updateDemo(d.id, {
        title: title.trim(), intro: intro.trim() || null, client_name: client.trim(),
        client_website: website.trim() || null, expires_at: endOfDay(expires), page_quota: q, qa_enabled: qa,
      });
      setDemo(updated); fillForm(updated); setNotice("Saved.");
    } catch (x) { setError(x instanceof Error ? x.message : String(x)); }
    finally { setSaving(false); }
  }

  async function toggle() {
    setConfirmToggle(false);
    try {
      const updated = await api.updateDemo(d.id, { enabled: !d.enabled });
      setDemo(updated);
      setNotice(updated.enabled ? "Demo turned on." : "Demo turned off. The page and its reader links no longer open.");
    } catch (x) { setError(x instanceof Error ? x.message : String(x)); }
  }

  async function crawl() {
    setConfirmCrawl(false);
    try { setDemo(await api.crawlDemoSite(d.id)); setNotice("Crawl started. The PDFs it finds appear in the Conversion plan."); }
    catch (x) { setError(x instanceof Error ? x.message : String(x)); }
  }

  async function addItem(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) { setAddErr("Give the document a label, e.g. \"Scanned contract (poor quality)\"."); return; }
    if (!file && !url.trim()) { setAddErr("Choose a PDF file or enter the PDF's web address."); return; }
    setAdding(true); setAddErr(null);
    try {
      await api.addDemoItem(d.id, { label: label.trim(), note: note.trim() || null, url: url.trim() || null, file });
      setLabel(""); setNote(""); setUrl(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setNotice("Document added. Nothing has been converted yet.");
      await load(false);
    } catch (x) { setAddErr(x instanceof Error ? x.message : String(x)); }
    finally { setAdding(false); }
  }

  async function move(index: number, by: -1 | 1) {
    const order = [...d.items];
    const [it] = order.splice(index, 1);
    order.splice(index + by, 0, it);
    try {
      await Promise.all(order.map((x, i) => (x.position !== i ? api.updateDemoItem(d.id, x.id, { position: i }) : null)));
      await load(false);
    } catch (x) { setError(x instanceof Error ? x.message : String(x)); }
  }

  async function doRemove() {
    if (!removeFor) return;
    const it = removeFor;
    setRemoveFor(null);
    try { await api.removeDemoItem(d.id, it.id); await load(false); setNotice(`${it.label} removed from the demo.`); }
    catch (x) { setError(x instanceof Error ? x.message : String(x)); }
  }

  async function doDelete() {
    setConfirmDelete(false);
    try { await api.deleteDemo(d.id); navigate("/demos"); }
    catch (x) { setError(x instanceof Error ? x.message : String(x)); }
  }

  const quotaText = d.tenant.page_quota != null
    ? `${d.tenant.pages_used.toLocaleString()} of ${d.tenant.page_quota.toLocaleString()} pages used`
    : `${d.tenant.pages_used.toLocaleString()} pages used`;

  return (
    <div>
      <Link to="/demos" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:underline mb-3">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All demos
      </Link>
      <PageHeader
        title={<span className="flex flex-wrap items-center gap-2">
          {d.client_name} <span className={st.pill}>{st.label}</span>
          {!d.owns_workspace && (
            <span className="pill-succeeded inline-flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" /> Customer</span>
          )}
        </span>}
        description={<>
          {quotaText} · ${d.usd_spent.toFixed(2)} spent · {d.view_count} view{d.view_count === 1 ? "" : "s"}
          {d.last_viewed_at && <> · last viewed {new Date(d.last_viewed_at).toLocaleString()}</>}
        </>}
        actions={<>
          <CopyLink url={d.url} />
          <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-secondary">
            <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open page<span className="sr-only"> (new tab)</span>
          </a>
          <button type="button" className="btn-secondary" onClick={() => setThemeFor(d.tenant)}>
            <Palette className="h-4 w-4" aria-hidden="true" /> Branding
          </button>
          <button type="button" className={d.enabled ? "btn-secondary" : "btn-primary"} onClick={() => setConfirmToggle(true)}>
            {d.enabled ? "Turn off" : "Turn on"}
          </button>
        </>}
      >
        <p className="text-sm mt-1 break-all"><a href={d.url} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">{d.url}</a></p>
      </PageHeader>

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {/* Documents */}
      <section className="card mb-6" aria-labelledby="docs-title">
        <div className="card-header">
          <h2 id="docs-title" className="text-base font-semibold text-ink">Documents ({d.items.length})</h2>
          <p className="text-xs text-slate-500 mt-1">
            Only documents marked <em>On the page</em> appear on the demo. Anything held for review stays off until you approve it on the Review screen.
          </p>
        </div>
        {d.items.length === 0 ? (
          <div className="card-body text-sm text-slate-500">No documents yet. Add the client's PDFs below.</div>
        ) : (
          <ol className="divide-y divide-slate-200">
            {d.items.map((it, i) => {
              const converted = it.conversion_status === "succeeded" || it.conversion_status === "degraded";
              const active = it.conversion_status === "queued" || it.conversion_status === "running";
              return (
                <li key={it.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-ink">{it.label}</div>
                      {it.title && <div className="text-sm text-slate-700">“{it.title}”</div>}
                      <div className="text-xs text-slate-500 mt-0.5 break-all">
                        {it.filename} · {it.page_count} page{it.page_count === 1 ? "" : "s"} · {fileSize(it.size_bytes)}
                        {it.is_scanned && " · scanned"}
                        {it.is_tagged === false && " · untagged"}
                        {it.has_form && " · has form fields"}
                        {it.page_range && <> · pages {rangeText(it.page_range)} only</>}
                        {it.estimated_cost_usd > 0 && <> · ${it.estimated_cost_usd.toFixed(2)}</>}
                      </div>
                      {it.published && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          Opened {it.open_count} time{it.open_count === 1 ? "" : "s"}
                          {it.last_opened_at && <> · last {new Date(it.last_opened_at).toLocaleString()}</>}
                        </div>
                      )}
                      {it.note && <p className="text-sm text-slate-600 mt-1">{it.note}</p>}
                      <div className="mt-2"><ItemStatus item={it} /></div>
                      {it.conversion_status === "failed" && it.error && <p className="text-xs text-rose-700 mt-1">{it.error}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <button type="button" className={it.conversion_id ? "btn-secondary" : "btn-primary"}
                        onClick={() => setConvertFor(it)} disabled={active}>
                        <Play className="h-4 w-4" aria-hidden="true" />
                        {it.conversion_id ? "Convert again…" : "Convert…"}<span className="sr-only"> {it.label}</span>
                      </button>
                      {converted && it.conversion_id && (
                        <Link to={`/workbench/${it.conversion_id}/review`} className="btn-secondary">
                          Review<span className="sr-only"> {it.label}</span>
                        </Link>
                      )}
                      {it.published && (
                        <a href={`${d.url}#open-${anchorOf(it)}`} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                          <ExternalLink className="h-4 w-4" aria-hidden="true" /> View<span className="sr-only"> {it.label} on the demo page (new tab)</span>
                        </a>
                      )}
                      <button type="button" className="btn-ghost" onClick={() => setEditFor(it)} aria-label={`Edit ${it.label}`}>
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${it.label} up`}>
                        <ArrowUp className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => move(i, 1)} disabled={i === d.items.length - 1} aria-label={`Move ${it.label} down`}>
                        <ArrowDown className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" className="btn-ghost text-rose-600" onClick={() => setRemoveFor(it)} aria-label={`Remove ${it.label}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Add a document */}
      <form onSubmit={addItem} className="card mb-6" noValidate aria-labelledby="add-title">
        <div className="card-header">
          <h2 id="add-title" className="text-base font-semibold text-ink flex items-center gap-2">
            <FilePlus2 className="h-4 w-4 text-brand-500" aria-hidden="true" /> Add a PDF
          </h2>
          <p className="text-xs text-slate-500 mt-1">Stores the PDF and counts its pages. Nothing is converted until you choose Convert.</p>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-4">
            <label htmlFor="ad-label" className="block text-sm font-medium text-slate-700 mb-1">
              Label <span className="text-rose-600" aria-hidden="true">*</span>
            </label>
            <input id="ad-label" className="input" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="Scanned contract (poor quality)" required aria-required="true" />
          </div>
          <div className="md:col-span-8">
            <label htmlFor="ad-note" className="block text-sm font-medium text-slate-700 mb-1">Note for the client</label>
            <input id="ad-note" className="input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Handwritten notes on pages 1, 13 and 14 are read into text." />
          </div>
          <div className="md:col-span-7">
            <label htmlFor="ad-url" className="block text-sm font-medium text-slate-700 mb-1">PDF web address</label>
            <input id="ad-url" className="input" type="url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://wheatonparkdistrict.com/wp-content/uploads/RES-2026-01.pdf" aria-describedby="ad-url-help" />
            <p id="ad-url-help" className="mt-1 text-xs text-slate-500">
              Downloaded now, and used as the "Original PDF" link. With a file too, the file is used and this stays the link.
            </p>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="ad-file" className="block text-sm font-medium text-slate-700 mb-1">or a PDF file</label>
            <input id="ad-file" ref={fileRef} className="block w-full text-sm" type="file" accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <div className="md:col-span-2 flex md:pt-6">
            <button type="submit" className="btn-primary w-full" disabled={adding}>
              {adding ? <><span className="spinner" aria-hidden="true" /> Adding…</> : "Add"}
            </button>
          </div>
          {addErr && <p role="alert" className="md:col-span-12 text-sm text-rose-700">{addErr}</p>}
        </div>
      </form>

      <ClientSiteCard demo={d} onCrawl={() => setConfirmCrawl(true)} />

      {customerResult && (
        <div role="status" className="card mb-6">
          <div className="card-body text-sm">
            <p className="font-medium text-emerald-800">{d.tenant.name} is now a customer, on {customerResult.site_domain}.</p>
            {customerResult.moved.length > 0 && <p className="mt-1">Served on their website: {customerResult.moved.join(", ")}.</p>}
            {customerResult.demo_only.length > 0 && <p className="mt-1">Demo-only (part of a PDF): {customerResult.demo_only.join(", ")}.</p>}
            <p className="mt-1">
              Next: invite their people from <Link to="/superadmin" className="text-blue-700 hover:underline">Super Admin</Link>, and send them the helper.js snippet.
            </p>
          </div>
        </div>
      )}

      {/* Page settings */}
      <form onSubmit={save} className="card mb-6" noValidate aria-labelledby="set-title">
        <div className="card-header">
          <h2 id="set-title" className="text-base font-semibold text-ink">Page settings</h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <label htmlFor="st-title" className="block text-sm font-medium text-slate-700 mb-1">Page title</label>
            <input id="st-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="st-client" className="block text-sm font-medium text-slate-700 mb-1">Client name</label>
            <input id="st-client" className="input" value={client} onChange={(e) => setClient(e.target.value)} />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="st-site" className="block text-sm font-medium text-slate-700 mb-1">Client website</label>
            <input id="st-site" className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>
          <div className="md:col-span-12">
            <label htmlFor="st-intro" className="block text-sm font-medium text-slate-700 mb-1">Introduction</label>
            <textarea id="st-intro" className="input min-h-[6rem]" value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="A short note to the client. Leave a blank line between paragraphs." />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="st-exp" className="block text-sm font-medium text-slate-700 mb-1">Available until</label>
            <input id="st-exp" className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
              aria-describedby="st-exp-help" />
            <p id="st-exp-help" className="mt-1 text-xs text-slate-500">Blank = no end date.</p>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="st-quota" className="block text-sm font-medium text-slate-700 mb-1">Page limit</label>
            <input id="st-quota" className="input" type="number" min={1} value={quota} onChange={(e) => setQuota(e.target.value)} />
          </div>
          <div className="md:col-span-4 flex items-center md:pt-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={qa} onChange={(e) => setQa(e.target.checked)} />
              Let the client ask questions about documents (each question costs a little)
            </label>
          </div>
          <div className="md:col-span-2 flex md:pt-6">
            <button type="submit" className="btn-primary w-full" disabled={saving}>
              {saving ? <><span className="spinner" aria-hidden="true" /> Saving…</> : "Save"}
            </button>
          </div>
        </div>
      </form>

      <div className="flex flex-wrap justify-between gap-3">
        {d.owns_workspace ? (
          <button type="button" className="btn-secondary" onClick={() => setMakeCustomerOpen(true)}>
            <BadgeCheck className="h-4 w-4" aria-hidden="true" /> Make this a customer…
          </button>
        ) : <span />}
        <button type="button" className="btn-ghost text-rose-600" onClick={() => setConfirmDelete(true)}>
          <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete demo
        </button>
      </div>

      {convertFor && (
        <ConvertModal
          demo={d}
          item={convertFor}
          onClose={() => setConvertFor(null)}
          onDone={(it) => {
            setConvertFor(null);
            setNotice(`${it.label}: conversion queued.`);
            load(false);
          }}
        />
      )}
      <PromptModal
        open={!!editFor}
        onClose={() => setEditFor(null)}
        title={`Edit ${editFor?.label ?? ""}`}
        fields={editFields}
        submitLabel="Save"
        onSubmit={async (v) => {
          if (!editFor) return;
          await api.updateDemoItem(d.id, editFor.id, {
            label: v.label.trim(), title: v.title.trim() || null, note: v.note.trim() || null,
            source_url: v.source_url.trim() || null,
          });
          setEditFor(null);
          await load(false);
        }}
      />
      <ConfirmModal
        open={!!removeFor}
        onClose={() => setRemoveFor(null)}
        onConfirm={doRemove}
        title={`Remove ${removeFor?.label ?? ""}?`}
        message="Takes the document off the demo page. Its conversion is kept."
        confirmLabel="Remove"
        tone="danger"
      />
      <ConfirmModal
        open={confirmToggle}
        onClose={() => setConfirmToggle(false)}
        onConfirm={toggle}
        title={d.enabled ? "Turn this demo off?" : "Turn this demo on?"}
        message={!d.owns_workspace
          ? (d.enabled ? "Hides the demo page. Their workspace and website stay live." : "Shows the demo page again.")
          : d.enabled
            ? "The page, every reader link from it and the client's login stop working. You can turn it back on."
            : "The page, its reader links and the client's login work again."}
        confirmLabel={d.enabled ? "Turn off" : "Turn on"}
        tone={d.enabled ? "warning" : "primary"}
      />
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title={`Delete the ${d.client_name} demo?`}
        message={d.owns_workspace
          ? "The page stops working and its workspace is suspended. Conversions already paid for are kept."
          : "Removes the demo page only. Their workspace and website stay live."}
        confirmLabel="Delete demo"
        tone="danger"
      />
      <ConfirmModal
        open={confirmCrawl}
        onClose={() => setConfirmCrawl(false)}
        onConfirm={crawl}
        title={`Crawl ${d.client_site?.domain ?? d.client_website ?? "the website"}?`}
        message="Finds every PDF on the client's website and reads its details: it downloads each PDF once from their server, and a very small AI call labels the ones the rules can't. Nothing is converted, and it won't run again unless you ask."
        confirmLabel="Crawl website"
      />
      {makeCustomerOpen && (
        <MakeCustomerModal
          demo={d}
          onClose={() => setMakeCustomerOpen(false)}
          onDone={(r) => { setMakeCustomerOpen(false); setCustomerResult(r); setDemo(r.demo); fillForm(r.demo); }}
        />
      )}
      <TenantThemeModal
        tenant={themeFor}
        onClose={() => setThemeFor(null)}
        onSaved={() => { setThemeFor(null); load(false); setNotice("Branding saved."); }}
      />
    </div>
  );
}
